import { useEffect, useState } from 'react'
import { Alert, Button, Flex, Form, Modal, Select, Space, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { AgentSelect } from '@/features/agent/AgentSelect'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel } from '@/features/branch/branchApi'
import { fetchAllUsers } from '@/features/identity/identityApi'
import {
  OVERRIDE_MODES,
  fetchPlanAccess,
  setPlanBranches,
  setPlanOverrides,
  type OverrideMode,
  type OverrideSubject,
  type PlanAccessOverride,
} from './planAccessApi'

interface Row {
  key: number
  subjectType: OverrideSubject
  subjectId: string | null
  mode: OverrideMode
}

/** Paket qaysi filiallarda va kimga sotiladi (ADR-047) */
export function PlanAccessModal({
  plan,
  onClose,
}: {
  plan: { id: string; name: string } | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const { options: branchChoices } = useActiveBranches()
  const [branchIds, setBranchIds] = useState<string[]>([])
  const [rows, setRows] = useState<Row[]>([])
  const open = plan !== null

  const access = useQuery({
    queryKey: queryKeys.catalog.planAccess(plan?.id ?? ''),
    queryFn: ({ signal }) => fetchPlanAccess(plan?.id ?? '', signal),
    enabled: open,
  })
  const users = useQuery({
    queryKey: queryKeys.identity.users,
    queryFn: fetchAllUsers,
    enabled: open && can('user.read'),
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (!access.data) return
    setBranchIds([...access.data.branchIds])
    setRows(access.data.overrides.map((o, index) => ({ key: index, ...o })))
  }, [access.data])

  const save = useMutation({
    mutationFn: async () => {
      const id = plan?.id ?? ''
      await setPlanBranches(id, branchIds)
      const overrides: PlanAccessOverride[] = rows
        .filter((row): row is Row & { subjectId: string } => row.subjectId !== null)
        .map(({ subjectType, subjectId, mode }) => ({ subjectType, subjectId, mode }))
      await setPlanOverrides(id, overrides)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all })
      onClose()
    },
  })

  const patchRow = (key: number, patch: Partial<Row>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  return (
    <Modal
      open={open}
      width={720}
      title={t('planAccess.title', { name: plan?.name ?? '' })}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={onClose}
      onOk={() => save.mutate()}
      destroyOnHidden
      afterOpenChange={(visible) => {
        if (visible) save.reset()
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {access.isError ? <Alert type="error" showIcon message={translateError(access.error, t)} /> : null}
        {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
        <Form layout="vertical" disabled={save.isPending || access.isPending}>
          <Form.Item label={t('branch.labelPlural')} extra={t('planAccess.branchesHint')}>
            <Select<string[]>
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={t('planAccess.allBranches')}
              value={branchIds}
              onChange={setBranchIds}
              options={branchChoices.map((b) => ({ value: b.id, label: branchLabel(b) }))}
            />
          </Form.Item>
          <Form.Item label={t('planAccess.overrides')} extra={t('planAccess.overridesHint')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {rows.map((row) => (
                <Flex key={row.key} gap={8} align="center" wrap>
                  <Select<OverrideSubject>
                    style={{ width: 120 }}
                    value={row.subjectType}
                    onChange={(subjectType) => patchRow(row.key, { subjectType, subjectId: null })}
                    options={[
                      { value: 'NODE', label: t('planAccess.subject.NODE') },
                      { value: 'USER', label: t('planAccess.subject.USER') },
                    ]}
                  />
                  <div style={{ flex: 1, minWidth: 200 }}>
                    {row.subjectType === 'NODE' ? (
                      <AgentSelect
                        value={row.subjectId}
                        onChange={(subjectId) => patchRow(row.key, { subjectId })}
                        placeholder={t('planAccess.chooseAgent')}
                      />
                    ) : (
                      <Select<string>
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        loading={users.isPending}
                        placeholder={t('planAccess.chooseUser')}
                        value={row.subjectId ?? undefined}
                        onChange={(subjectId) => patchRow(row.key, { subjectId })}
                        options={(users.data ?? []).map((u) => ({ value: u.id, label: `${u.displayName} (${u.username})` }))}
                      />
                    )}
                  </div>
                  <Select<OverrideMode>
                    style={{ width: 130 }}
                    value={row.mode}
                    onChange={(mode) => patchRow(row.key, { mode })}
                    options={OVERRIDE_MODES.map((mode) => ({ value: mode, label: t(`planAccess.mode.${mode}`) }))}
                  />
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label={t('actions.delete')}
                    onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                  />
                </Flex>
              ))}
              <Button
                icon={<PlusOutlined />}
                onClick={() =>
                  setRows((current) => [
                    ...current,
                    { key: Date.now(), subjectType: 'NODE', subjectId: null, mode: 'ALLOW' },
                  ])
                }
              >
                {t('planAccess.addOverride')}
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('planAccess.denyWins')}
              </Typography.Text>
            </Space>
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
